<template>
  <div class="container mx-auto px-4 py-8">
    <!-- Category Header -->
    <div class="mb-8">
      <h1 class="text-3xl md:text-4xl font-display font-bold text-primary-800 mb-4 capitalize">
        {{ categoryName }}
      </h1>
      <p v-if="category?.description" class="text-lg text-primary-600 max-w-2xl">
        {{ category.description }}
      </p>
    </div>

    <!-- Enhanced Product Grid with Controls -->
    <ProductGrid
      :products="products"
      :loading="loading"
      :columns="{
        xs: 1,
        sm: 2,
        md: 2,
        lg: 3,
        xl: 4,
        xxl: 5
      }"
      :gap="{
        xs: '1rem',
        sm: '1.25rem',
        md: '1.5rem',
        lg: '2rem',
        xl: '2rem',
        xxl: '2.5rem'
      }"
      :show-controls="true"
      :show-rating="true"
      :lazy-loading="true"
      :infinite-scroll="true"
      :items-per-page="12"
      :empty-message="`ไม่มีสินค้าในหมวดหมู่ ${categoryName}`"
      :show-reset-button="false"
      @add-to-cart="handleAddToCart"
      @add-to-wishlist="handleAddToWishlist"
      @quick-view="handleQuickView"
      @sort-change="handleSortChange"
      @grid-change="handleGridChange"
      @load-more="handleLoadMore"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '../api';
import ProductGrid from '../components/ProductGrid.vue';
import { useNotification } from '../composables/useNotification';
import { useCartStore } from '../stores/cart';

const route = useRoute();
const router = useRouter();
const products = ref([]);
const category = ref(null);
const loading = ref(true);
const currentPage = ref(1);
const sortBy = ref('default');

const { showSuccess, showError } = useNotification();
const cartStore = useCartStore();

const categorySlug = computed(() => route.params.slug);
const categoryName = computed(() => {
  if (category.value?.name) {
    return category.value.name;
  }
  return categorySlug.value.replace(/-/g, ' ');
});

const fetchProducts = async (page = 1, sort = 'default', append = false) => {
  if (!append) {
    loading.value = true;
  }
  
  try {
    const params = {
      page,
      sort,
      per_page: 12
    };
    
    const [productsResponse, categoryResponse] = await Promise.all([
      api.get(`/products/category/${categorySlug.value}`, { params }),
      api.get(`/categories/${categorySlug.value}`)
    ]);
    
    if (append) {
      products.value = [...products.value, ...productsResponse.data.data];
    } else {
      products.value = productsResponse.data.data;
    }
    
    category.value = categoryResponse.data.data;
    currentPage.value = page;
  } catch (error) {
    console.error('Failed to fetch products:', error);
    showError('ไม่สามารถโหลดสินค้าได้');
    if (!append) {
      products.value = [];
    }
  } finally {
    loading.value = false;
  }
};

// Enhanced event handlers
const handleAddToCart = async (product) => {
  try {
    await cartStore.addToCart(product.id, null, 1);
    showSuccess(`เพิ่ม "${product.name}" ลงตะกร้าแล้ว`);
  } catch (error) {
    console.error('Add to cart error:', error);
    showError('ไม่สามารถเพิ่มสินค้าได้');
  }
};

const handleAddToWishlist = (product) => {
  showSuccess(`เพิ่ม "${product.name}" ลงรายการโปรดแล้ว`);
};

const handleQuickView = (product) => {
  router.push(`/product/${product.slug}`);
};

const handleSortChange = (newSort) => {
  sortBy.value = newSort;
  fetchProducts(1, newSort, false);
};

const handleGridChange = (columns) => {
  // Store grid preference in localStorage
  localStorage.setItem('preferredGridColumns', columns);
};

const handleLoadMore = () => {
  fetchProducts(currentPage.value + 1, sortBy.value, true);
};

onMounted(() => {
  fetchProducts();
});

watch(categorySlug, () => {
  fetchProducts();
});
</script>
